import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock,
  FaWhatsapp, FaInstagram, FaFacebookF, FaArrowRight,
  FaGem, FaCheckCircle, FaPaperPlane, FaChevronDown
} from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

const Contact = () => {
  const { isDarkMode } = useTheme();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    interest: ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const interestOptions = [
    'Gold Jewellery', 'Diamond Jewellery', 'Silver Jewellery',
    'Platinum Jewellery', 'Custom Design', 'Repair & Polish', 'Other'
  ];

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1800));
    setLoading(false);
    setSubmitted(true);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    visible: (i = 0) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.65, delay: i * 0.11, ease: [0.22, 1, 0.36, 1] }
    })
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      label: 'Call Us',
      value: '+91 95255 63933',
      sub: 'Mon–Sat, 10am – 8pm',
      href: 'tel:+919876543210',
      color: '#22c55e'
    },
    {
      icon: <FaWhatsapp />,
      label: 'WhatsApp',
      value: '+91 95255 63933',
      sub: 'Quick replies guaranteed',
      href: 'https://wa.me/919876543210',
      color: '#25d366'
    },
    {
      icon: <FaEnvelope />,
      label: 'Email Us',
      value: 'info@bhartijewellers.com',
      sub: 'We reply within 24 hrs',
      href: 'mailto:info@bhartijewellers.com',
      color: '#d4af37'
    },
    {
      icon: <FaMapMarkerAlt />,
      label: 'Visit Us',
      value: 'Main Market, Gorakhpur',
      sub: 'Uttar Pradesh, India',
      href: '#',
      color: '#f87171'
    }
  ];

  const timings = [
    { day: 'Mon – Fri', time: '10:00 AM – 8:00 PM' },
    { day: 'Saturday', time: '10:00 AM – 9:00 PM' },
    { day: 'Sunday', time: '11:00 AM – 7:00 PM' }
  ];

  // ── Styles ──
  const gold = 'var(--primary-gold, #d4af37)';
  const gradGold = 'var(--gradient-gold, linear-gradient(135deg, #b8860b, #d4af37, #f5d782))';

  const s = {
    page: {
      background: isDarkMode ? 'var(--bg-primary)' : '#faf9f7',
      minHeight: '100vh',
      overflowX: 'hidden'
    },
    // Hero
    hero: {
      position: 'relative',
      padding: '80px 0 60px',
      overflow: 'hidden',
      background: isDarkMode
        ? 'linear-gradient(160deg, #0d0b06 0%, #1a1508 50%, #0d0b06 100%)'
        : 'linear-gradient(160deg, #1a1508 0%, #2e230a 50%, #1a1508 100%)'
    },
    heroBadge: {
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      background: 'rgba(212,175,55,0.15)',
      border: '1px solid rgba(212,175,55,0.4)',
      borderRadius: '50px', padding: '6px 18px',
      fontSize: '12px', fontWeight: '700', letterSpacing: '2.5px',
      textTransform: 'uppercase', color: '#d4af37', marginBottom: '20px'
    },
    heroTitle: {
      fontFamily: 'var(--font-secondary, serif)',
      fontSize: 'clamp(2.2rem, 5.5vw, 3.6rem)',
      fontWeight: '900', color: '#fff',
      lineHeight: 1.15, marginBottom: '16px'
    },
    heroSub: {
      fontSize: '16px', color: 'rgba(255,255,255,0.7)',
      maxWidth: '500px', lineHeight: '1.75'
    },
    // Form card
    formCard: {
      background: isDarkMode ? 'var(--bg-secondary)' : '#ffffff',
      borderRadius: '24px',
      border: isDarkMode ? '1px solid rgba(212,175,55,0.12)' : '1px solid rgba(0,0,0,0.07)',
      boxShadow: isDarkMode ? '0 0 0 transparent' : '0 8px 50px rgba(0,0,0,0.08)',
      padding: '44px 40px',
      position: 'relative', overflow: 'hidden'
    },
    // Input field
    inputWrapper: (name) => ({
      position: 'relative',
      marginBottom: '20px'
    }),
    label: {
      display: 'block',
      fontSize: '12px', fontWeight: '700',
      letterSpacing: '1.5px', textTransform: 'uppercase',
      color: 'var(--text-secondary)',
      marginBottom: '8px'
    },
    input: (name) => ({
      width: '100%',
      background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8f6f1',
      border: `1.5px solid ${focusedField === name
        ? 'rgba(212,175,55,0.6)'
        : isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: '12px',
      padding: '13px 18px',
      fontSize: '15px',
      color: 'var(--text-primary)',
      outline: 'none',
      transition: 'border-color 0.25s, box-shadow 0.25s',
      boxShadow: focusedField === name ? '0 0 0 4px rgba(212,175,55,0.1)' : 'none',
      fontFamily: 'inherit'
    }),
    select: (name) => ({
      width: '100%',
      background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8f6f1',
      border: `1.5px solid ${focusedField === name
        ? 'rgba(212,175,55,0.6)'
        : isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: '12px',
      padding: '13px 18px',
      fontSize: '15px',
      color: formData[name] ? 'var(--text-primary)' : 'var(--text-secondary)',
      outline: 'none',
      transition: 'border-color 0.25s, box-shadow 0.25s',
      boxShadow: focusedField === name ? '0 0 0 4px rgba(212,175,55,0.1)' : 'none',
      cursor: 'pointer',
      appearance: 'none',
      fontFamily: 'inherit'
    }),
    textarea: (name) => ({
      width: '100%',
      background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8f6f1',
      border: `1.5px solid ${focusedField === name
        ? 'rgba(212,175,55,0.6)'
        : isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: '12px',
      padding: '13px 18px',
      fontSize: '15px',
      color: 'var(--text-primary)',
      outline: 'none',
      transition: 'border-color 0.25s, box-shadow 0.25s',
      boxShadow: focusedField === name ? '0 0 0 4px rgba(212,175,55,0.1)' : 'none',
      resize: 'vertical',
      minHeight: '120px',
      fontFamily: 'inherit',
      lineHeight: '1.6'
    }),
    submitBtn: {
      width: '100%',
      background: gradGold,
      border: 'none',
      borderRadius: '14px',
      padding: '16px 32px',
      fontSize: '15px', fontWeight: '700',
      letterSpacing: '1px', textTransform: 'uppercase',
      color: '#1a1200',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
      transition: 'opacity 0.2s, transform 0.15s',
      opacity: loading ? 0.75 : 1
    },
    // Info card
    infoCard: {
      background: isDarkMode
        ? 'linear-gradient(160deg, #1a1508 0%, #0d0b06 100%)'
        : 'linear-gradient(160deg, #1f1a07 0%, #2e230a 100%)',
      borderRadius: '24px',
      padding: '44px 36px',
      border: '1px solid rgba(212,175,55,0.2)',
      height: '100%'
    },
    contactItem: {
      display: 'flex', alignItems: 'flex-start', gap: '16px',
      padding: '18px 0',
      borderBottom: '1px solid rgba(212,175,55,0.1)'
    },
    contactIcon: (color) => ({
      width: '44px', height: '44px', borderRadius: '12px',
      background: `${color}18`,
      border: `1px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: color, flexShrink: 0, fontSize: '16px'
    }),
    timingRow: {
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', padding: '10px 0',
      borderBottom: '1px solid rgba(255,255,255,0.06)'
    },
    // Success
    successCard: {
      textAlign: 'center', padding: '60px 32px'
    }
  };

  return (
    <div style={s.page}>


      {/* ── MAIN CONTENT ── */}
      <section className="py-5">
        <div className="container">
          <div className="row g-4 align-items-start">

            {/* LEFT: Contact Info */}
            <div className="col-lg-4">
              <motion.div
                style={s.infoCard}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}>

                <div style={{
                  fontSize: '11px', fontWeight: '800', letterSpacing: '3px',
                  textTransform: 'uppercase', color: '#d4af37', marginBottom: '8px'
                }}>
                  Contact Details
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-secondary, serif)',
                  color: '#fff', fontWeight: '800',
                  fontSize: '22px', marginBottom: '28px'
                }}>
                  Bharti Jewellers
                </h3>

                {contactInfo.map((item, i) => (
                  <motion.a
                    key={i}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : '_self'}
                    rel="noreferrer"
                    variants={fadeUp}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    whileHover={{ x: 4 }}
                    style={{ ...s.contactItem, textDecoration: 'none' }}>
                    <div style={s.contactIcon(item.color)}>
                      {item.icon}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px',
                        textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
                        marginBottom: '3px'
                      }}>
                        {item.label}
                      </div>
                      <div style={{
                        fontSize: '14.5px', fontWeight: '600',
                        color: '#fff', marginBottom: '2px'
                      }}>
                        {item.value}
                      </div>
                      <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.5)' }}>
                        {item.sub}
                      </div>
                    </div>
                  </motion.a>
                ))}

                {/* Timings */}
                <div style={{ marginTop: '28px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    marginBottom: '16px'
                  }}>
                    <FaClock size={14} color="#d4af37" />
                    <span style={{
                      fontSize: '11px', fontWeight: '800',
                      letterSpacing: '2px', textTransform: 'uppercase',
                      color: '#d4af37'
                    }}>
                      Store Hours
                    </span>
                  </div>
                  {timings.map((t, i) => (
                    <div key={i} style={s.timingRow}>
                      <span style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.65)' }}>
                        {t.day}
                      </span>
                      <span style={{
                        fontSize: '13px', fontWeight: '600',
                        color: '#d4af37'
                      }}>
                        {t.time}
                      </span>
                    </div>
                  ))}
                </div>

              </motion.div>
            </div>

            {/* RIGHT: Form */}
            <div className="col-lg-8">
              <motion.div
                style={s.formCard}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}>

                {/* Gold accent top bar */}
                <div style={{
                  position: 'absolute', top: 0, left: '10%', right: '10%',
                  height: '3px', borderRadius: '0 0 4px 4px',
                  background: gradGold
                }} />

                <AnimatePresence mode="wait">
                  {submitted ? (
                    /* ── SUCCESS STATE ── */
                    <motion.div
                      key="success"
                      style={s.successCard}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                        style={{
                          width: '80px', height: '80px', borderRadius: '50%',
                          background: 'linear-gradient(135deg, #b8860b, #d4af37)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 24px',
                          boxShadow: '0 8px 32px rgba(212,175,55,0.4)'
                        }}>
                        <FaCheckCircle size={36} color="#1a1200" />
                      </motion.div>
                      <h3 style={{
                        fontFamily: 'var(--font-secondary, serif)',
                        fontSize: '28px', fontWeight: '800',
                        color: 'var(--text-primary)', marginBottom: '12px'
                      }}>
                        Message Sent!
                      </h3>
                      <p style={{
                        color: 'var(--text-secondary)', fontSize: '16px',
                        lineHeight: '1.7', maxWidth: '400px', margin: '0 auto 32px'
                      }}>
                        Thank you for reaching out, <strong style={{ color: gold }}>
                          {formData.name}
                        </strong>. Our team will get back to you within 24 hours.
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '', interest: '' }); }}
                        style={{
                          ...s.submitBtn,
                          width: 'auto', padding: '13px 32px',
                          margin: '0 auto'
                        }}>
                        Send Another Message
                      </motion.button>
                    </motion.div>
                  ) : (
                    /* ── FORM ── */
                    <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <div style={{ marginBottom: '32px' }}>
                        <p style={{
                          fontSize: '12px', fontWeight: '700',
                          letterSpacing: '2.5px', textTransform: 'uppercase',
                          color: gold, marginBottom: '6px'
                        }}>
                          Send a Message
                        </p>
                        <h2 style={{
                          fontFamily: 'var(--font-secondary, serif)',
                          fontSize: '26px', fontWeight: '800',
                          color: 'var(--text-primary)', margin: 0
                        }}>
                          How Can We Help You?
                        </h2>
                      </div>

                      <form onSubmit={handleSubmit}>
                        {/* Row 1 */}
                        <div className="row g-3 mb-1">
                          <div className="col-md-6">
                            <div style={s.inputWrapper('name')}>
                              <label style={s.label}>Full Name *</label>
                              <input
                                type="text" name="name" required
                                placeholder="Your full name"
                                value={formData.name}
                                onChange={handleChange}
                                onFocus={() => setFocusedField('name')}
                                onBlur={() => setFocusedField(null)}
                                style={s.input('name')}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div style={s.inputWrapper('phone')}>
                              <label style={s.label}>Phone Number</label>
                              <input
                                type="tel" name="phone"
                                placeholder="+91 00000 00000"
                                value={formData.phone}
                                onChange={handleChange}
                                onFocus={() => setFocusedField('phone')}
                                onBlur={() => setFocusedField(null)}
                                style={s.input('phone')}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div className="row g-3 mb-1">
                          <div className="col-md-6">
                            <div style={s.inputWrapper('email')}>
                              <label style={s.label}>Email Address *</label>
                              <input
                                type="email" name="email" required
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={handleChange}
                                onFocus={() => setFocusedField('email')}
                                onBlur={() => setFocusedField(null)}
                                style={s.input('email')}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div style={s.inputWrapper('interest')}>
                              <label style={s.label}>I'm Interested In</label>
                              <div style={{ position: 'relative' }}>
                                <select
                                  name="interest"
                                  value={formData.interest}
                                  onChange={handleChange}
                                  onFocus={() => setFocusedField('interest')}
                                  onBlur={() => setFocusedField(null)}
                                  style={s.select('interest')}>
                                  <option value="">Select category...</option>
                                  {interestOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <FaChevronDown style={{
                                  position: 'absolute', right: '16px',
                                  top: '50%', transform: 'translateY(-50%)',
                                  color: 'var(--text-secondary)', fontSize: '12px',
                                  pointerEvents: 'none'
                                }} />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Subject */}
                        <div style={s.inputWrapper('subject')}>
                          <label style={s.label}>Subject</label>
                          <input
                            type="text" name="subject"
                            placeholder="What's this about?"
                            value={formData.subject}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('subject')}
                            onBlur={() => setFocusedField(null)}
                            style={s.input('subject')}
                          />
                        </div>

                        {/* Message */}
                        <div style={s.inputWrapper('message')}>
                          <label style={s.label}>Your Message *</label>
                          <textarea
                            name="message" required
                            placeholder="Tell us about your requirements, preferred designs, budget, or any questions..."
                            value={formData.message}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('message')}
                            onBlur={() => setFocusedField(null)}
                            style={s.textarea('message')}
                          />
                        </div>

                        {/* Submit */}
                        <motion.button
                          type="submit"
                          disabled={loading}
                          whileHover={!loading ? { scale: 1.02, y: -1 } : {}}
                          whileTap={!loading ? { scale: 0.98 } : {}}
                          style={s.submitBtn}>
                          {loading ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                                style={{
                                  width: '18px', height: '18px',
                                  border: '2.5px solid rgba(26,18,0,0.25)',
                                  borderTop: '2.5px solid #1a1200',
                                  borderRadius: '50%'
                                }} />
                              Sending...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane size={14} />
                              Send Message
                              <FaArrowRight size={12} />
                            </>
                          )}
                        </motion.button>

                        <p style={{
                          textAlign: 'center', marginTop: '14px',
                          fontSize: '12.5px', color: 'var(--text-secondary)'
                        }}>
                          🔒 Your information is secure and never shared.
                        </p>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAP SECTION ── */}
      <section className="pb-5">
        <div className="container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}>
            <div style={{
              borderRadius: '20px', overflow: 'hidden',
              border: isDarkMode ? '1px solid rgba(212,175,55,0.15)' : '1px solid rgba(0,0,0,0.08)',
              boxShadow: isDarkMode ? 'none' : '0 8px 40px rgba(0,0,0,0.08)',
              position: 'relative'
            }}>
              {/* Map header overlay */}
              <div style={{
                position: 'absolute', top: '16px', left: '16px', zIndex: 10,
                background: isDarkMode ? 'rgba(13,11,6,0.92)' : 'rgba(255,255,255,0.95)',
                border: isDarkMode ? '1px solid rgba(212,175,55,0.2)' : '1px solid rgba(0,0,0,0.1)',
                borderRadius: '12px', padding: '12px 18px',
                display: 'flex', alignItems: 'center', gap: '10px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #b8860b, #d4af37)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FaMapMarkerAlt size={15} color="#1a1200" />
                </div>
                <div>
                  <div style={{
                    fontSize: '13px', fontWeight: '700',
                    color: 'var(--text-primary)'
                  }}>
                    Bharti Jewellers
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>
                    Main Market, Gorakhpur, UP
                  </div>
                </div>
              </div>

              <iframe
                title="Bharti Jewellers Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d114456.37671843!2d83.30932!3d26.75996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3991446a0c332499%3A0x1ff3f97fdcc6bfa2!2sGorakhpur%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="380"
                style={{ border: 'none', display: 'block', filter: isDarkMode ? 'invert(0.9) hue-rotate(180deg)' : 'none' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Contact;